import React, { JSX, useMemo } from 'react';
import {
  ApiBooking,
  ApiComponentType,
  ApiCustomer,
  ApiFlightItem,
  ApiFlightSegment,
  ApiHotel,
  ApiHotelRoom,
  ApiInsurance,
  ApiItemType,
  ApiRoomRate,
  ApiTransfer
} from '@ibe/api';
import { Col, Row } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBed,
  faBus as faBusSolid,
  faPlane as faPlaneSolid,
  faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import { decodeObjectBase64 } from '@ibe/services';
import BestPriceAvailabilityStore from '../../Modules/BestPriceAvailability/BestPriceAvailabilityStore';
import useConfig from '../../Hooks/useConfig';
import useAppService from '../../Hooks/useAppService';
import DateDisplay from '../UI/DateDisplay/DateDisplay';
import { Price } from '../UI/Price/Price';
import Duration from '../../Items/Package/List/Duration/Duration';
import useTranslation from '../../Hooks/useTranslation';
import fallback from '../../../_Translations/generated/booking-summary.en.t.json';
import Keys from '../../../_Translations/generated/booking-summary.en.t.json.keys';
import ItemKey from '../../../Models/ItemKey';

export interface BookingSummaryProps {
  booking: ApiBooking;
  store?: BestPriceAvailabilityStore;
  showFlightNumber?: boolean;
}

const BookingSummary = (props: BookingSummaryProps) => {
  const { booking, showFlightNumber } = props;

  const { t } = useTranslation('booking-summary', fallback, Keys);

  const config = useConfig();
  const app = useAppService();
  const format = config.displayFormatDate[app.lang] || 'DD/MM/YYYY';

  const hotelBookedItem = booking.bookedItems.filter(
    item => item.itemType === ApiItemType.HOTEL
  )[0];

  const hotelBookedItems = useMemo(() => {
    return booking?.bookedItems.filter(
      bookedItem => bookedItem.componentType === ApiComponentType.MAIN
    );
  }, [booking?.bookedItems]);

  const hotelItemId: string =
    decodeObjectBase64<ItemKey>(ItemKey, hotelBookedItem.idParent).idParent || '';

  const hotel = booking.items.find(item => item.id === hotelItemId) as ApiHotel;

  const flightsBookedItems = (booking.bookedItems.filter(
    item => item.itemType === ApiItemType.FLIGHT
  ) as unknown) as ApiFlightItem[];

  // position in package describes which flight is departure and which one is arrival
  const outboundFlightBookedItem = flightsBookedItems[0];
  const outboundFlight = booking.items.find(
    item => item.id === outboundFlightBookedItem.idParent
  ) as ApiFlightItem;
  const outboundSegment = outboundFlight.segment && outboundFlight.segment[0];

  const inboundFlightBookedItem = flightsBookedItems[1];
  const inboundFlight = booking.items.find(
    item => item.id === inboundFlightBookedItem.idParent
  ) as ApiFlightItem;
  const inboundSegment = inboundFlight.segment && inboundFlight.segment[0];

  const transfers = (booking.bookedItems.filter(
    item => item.itemType === ApiItemType.TRANSFER
  ) as unknown) as ApiTransfer[];
  const insurances = (booking.bookedItems.filter(
    item => item.itemType === ApiItemType.INSURANCE
  ) as unknown) as ApiInsurance[];

  const travelApplicant = booking.client as ApiCustomer & { agencyName: string };
  const startDate = booking.travelStartDate || '';
  const endDate = inboundSegment?.departure || booking.travelEndDate || '';
  const duration = hotel.durationInfo;

  const totalPrice = booking.price;

  function buildFlightRow(): JSX.Element {
    return (
      <Row className="booking-summary__items-summary__flight">
        <Col xs="auto">
          <FontAwesomeIcon
            key="booking-summary__items-summary__flight_icon"
            className="booking-summary__items-summary__flight_icon"
            icon={faPlaneSolid}
          />
        </Col>
        <Col>
          <Row>
            <Col>
              <span className="h5 font-weight-bold">
                {outboundSegment.origin.description} {outboundSegment.origin.code} -{' '}
                {outboundSegment.destination.description} {outboundSegment.destination.code}
                {' / '} {inboundSegment.origin.description} {inboundSegment.origin.code} -{' '}
                {inboundSegment.destination.description} {inboundSegment.destination.code}
              </span>
            </Col>
          </Row>
          {outboundSegment && buildFlightSegmentRow(outboundSegment)}
          {inboundSegment && buildFlightSegmentRow(inboundSegment)}
        </Col>
      </Row>
    );
  }

  function buildFlightSegmentRow(segment: ApiFlightSegment) {
    return (
      <Row className="align-items-center">
        {segment.marketingCarrier?.logoUrl && (
          <Col className="col-1 mr-1">
            {
              <img
                className="booking-summary__items-summary__flight__carrier__logo"
                src={segment.marketingCarrier?.logoUrl}
                alt="Airline logo"
              />
            }
          </Col>
        )}
        <Col className="carrier_name col-auto">{segment?.marketingCarrier?.name}</Col>
        <Col className="col-auto px-0">
          <DateDisplay utc date={segment.departure} format={format} stripTime />
        </Col>
        <Col className="col-auto booking-summary__items-summary__item">
          <span className="booking-summary__items-summary__airport-code mr-1">
            {segment.origin.code}
          </span>
          <span className="booking-summary__items-summary__flight-time">
            <DateDisplay utc format={'HH:mm'} date={segment.departure} />
          </span>
        </Col>
        <Col className="col-1 px-0 align-self-center">
          <div className="booking-summary__items-summary__flightLogo" />
        </Col>
        <Col className="col-auto booking-summary__items-summary__item">
          <span className="booking-summary__items-summary__airport-code mr-1">
            {segment.destination.code}
          </span>
          <span className="booking-summary__items-summary__flight-time">
            <DateDisplay utc format={'HH:mm'} date={segment.arrival} />
            {segment?.dateDeviation && segment?.dateDeviation > 0 ? (
              <sup>+{segment?.dateDeviation}</sup>
            ) : null}
          </span>
          {showFlightNumber && segment.flightNumber && segment.flightCode && (
            <span className="booking-summary__items-summary__flight-number">
              {`(${segment.flightCode}${segment.flightNumber})`}
            </span>
          )}
        </Col>
      </Row>
    );
  }

  function buildHotelRow(): JSX.Element {
    return (
      <Row className="booking-summary__items-summary__hotel">
        <Col xs="auto">
          <div>
            <FontAwesomeIcon
              key="booking-summary__items-summary__hotel_icon"
              className="booking-summary__items-summary__hotel_icon"
              icon={faBed}
            />
          </div>
        </Col>
        <Col>
          <Row>
            <Col xs="auto">
              <span className="h5 font-weight-bold">{hotel.name}</span>
            </Col>
            {duration && (
              <Col className="col-2">
                {
                  <span>
                    <Duration info={duration} />
                  </span>
                }
              </Col>
            )}
            <Col xs="auto">
              <span>
                <DateDisplay utc date={startDate} format={format} /> -{' '}
                <DateDisplay utc date={endDate} format={format} />
              </span>
            </Col>
          </Row>
          {hotelBookedItems.map((bookedItemsHotel, idx) => {
            const bookedRoomRate = booking?.items.find(
              item => item.id === bookedItemsHotel?.idParent
            ) as ApiRoomRate;
            const bookedRoom = booking?.items.find(
              item => item.id === bookedRoomRate?.idParent
            ) as ApiHotelRoom;
            return (
              <div
                key={bookedItemsHotel.id}
                className={idx === hotelBookedItems.length - 1 ? '' : 'mb-3'}
              >
                <Row className="mb-2">
                  <Col xs={12}>
                    <div>{bookedRoom.name}</div>
                    <div>{bookedRoomRate.mealTypeDescription}</div>
                  </Col>
                </Row>
                {booking.travelers.map(traveler => {
                  if (bookedItemsHotel.priceByPersonId[traveler.id]) {
                    return (
                      <Row key={traveler.id} className="no-gutters">
                        <Col>
                          {traveler.salutation} {traveler.firstName} {traveler.lastName}
                        </Col>
                      </Row>
                    );
                  }
                })}
              </div>
            );
          })}
        </Col>
      </Row>
    );
  }

  function buildTransferRow(): JSX.Element {
    return (
      <Row className="booking-summary__items-summary__transfer">
        <Col xs="auto">
          <FontAwesomeIcon
            key="booking-summary__items-summary__transfer_icon"
            className="booking-summary__items-summary__transfer_icon"
            icon={faBusSolid}
          />
        </Col>
        <Col>
          <Row>
            <Col>
              <div className="h5 font-weight-bold">{t(Keys.transfer)}</div>
              {transfers.map(transfer => {
                const unit = (booking.items.filter(
                  item => item.itemType === ApiItemType.TRANSFER
                ) as ApiTransfer[])
                  .flatMap(tr => tr.units)
                  .find(tr => tr.id === transfer.idParent);
                if (transfer.name) {
                  return (
                    <div key={transfer.id}>
                      {transfer.name} {` ${unit?.name || ''}`}{' '}
                    </div>
                  );
                }
              })}
            </Col>
          </Row>
        </Col>
      </Row>
    );
  }

  function buildInsuranceRow(): JSX.Element {
    return (
      <Row className="booking-summary__items-summary__insurance">
        <Col xs="auto">
          <FontAwesomeIcon
            key="booking-summary__items-summary__insurance_icon"
            className="booking-summary__items-summary__insurance_icon"
            icon={faShieldAlt}
          />
        </Col>
        <Col>
          <Row>
            <Col>
              <div className="h5 font-weight-bold">{t(Keys.insurance)}</div>
              {insurances.map(insurance => {
                const unit = (booking.items.filter(
                  item => item.itemType === ApiItemType.INSURANCE
                ) as ApiInsurance[])
                  .flatMap(tr => tr.units)
                  .find(tr => tr.id === insurance.idParent);
                if (insurance.name) {
                  return (
                    <div key={insurance.id}>
                      {insurance.name}
                      {` ${unit?.name || ''}`}
                    </div>
                  );
                }
              })}
            </Col>
          </Row>
        </Col>
      </Row>
    );
  }
  const isAgency = travelApplicant.type === 'Agency';
  return (
    <Row className="booking-summary__container px-2 pb-1">
      <Col>
        <Row className="booking-summary__travelers-summary">
          <Col className="col-sm-12 col-md-6">
            <Row>
              <Col xs={12}>
                <span className="h5 font-weight-bold">{t(Keys.travelApplicant)}</span>
              </Col>
            </Row>
            <Row>
              <Col className="col-auto">
                {isAgency ? (
                  <div>{travelApplicant.agencyName}</div>
                ) : (
                  <>
                    <div>{travelApplicant.salutation}</div>
                    <div>{travelApplicant.firstName + ' ' + travelApplicant.lastName}</div>
                  </>
                )}
                <div>
                  {(travelApplicant?.address?.street
                    ? travelApplicant?.address?.street
                    : '' + ' ') +
                    (travelApplicant?.address?.houseNumber
                      ? travelApplicant?.address?.houseNumber
                      : '')}
                </div>
                <div>
                  {travelApplicant?.address?.postalCode + ' ' + travelApplicant?.address?.city}
                </div>
              </Col>
              <Col className="col-6">
                <div>{travelApplicant.communicationDetails.email}</div>
                <div>
                  {travelApplicant.communicationDetails.phone ||
                    travelApplicant.communicationDetails.mobilePhone}
                </div>
                <div>{travelApplicant?.address?.countryCode}</div>
              </Col>
            </Row>
          </Col>
          <Col className="col-sm-12 col-md-6 mt-sm-4 mt-md-0">
            <Row>
              <Col xs={12}>
                <span className="h5 font-weight-bold">{t(Keys.travelParticipants)}</span>
              </Col>
            </Row>
            <Row>
              {booking.travelers.map((traveler, index) => (
                <Col key={traveler.id + index} xs={12}>
                  <span className="mr-2">
                    {index + 1 + '. ' + t(Keys[(traveler.type as unknown) as keyof typeof Keys])}
                  </span>
                  <span className="mx-2">{traveler.salutation}</span>
                  <span className="ml-2">{traveler.firstName + ' ' + traveler.lastName}</span>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
        <Row className="booking-summary__items-summary">
          <Col>
            {buildFlightRow()}
            {buildHotelRow()}
            {transfers && transfers.length && buildTransferRow()}
            {insurances && insurances.length && buildInsuranceRow()}
            <Row className="mt-5 mb-2 booking-summary__price-total_container">
              <Col>
                <Row className="px-3">
                  <span className="mr-1">{t(Keys.totalPrice)}</span>
                  <Price
                    className="booking-summary__price-total font-weight-bold"
                    price={totalPrice}
                    align="left"
                  />
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default BookingSummary;
